import { type CustomerSearchResult } from "../../../CustomersPage/types/customer.types.ts"
import { type LeadFormDataCustomer } from "../../LeadAddPage/components/lead-form-data.ts"
import { type Lead, type LeadCustomer } from "../../types/lead.types.ts"

/**
 * The customer a lead reads and writes: a linked record when there is one, the
 * unlinked draft otherwise. Both sit under `customer` on the write, exactly as
 * the edit form sends them.
 */
const leadCustomerRecord = (lead: Lead | null): LeadCustomer | null =>
  lead?.customer || lead?.temporary_customer || null

const isLinkedCustomer = (lead: Lead | null) => !!lead?.customer

/**
 * `status` is deliberately absent: the API serializes it as a label ("No
 * Access") but validates it as a raw choice value, so echoing it back is
 * rejected. It is derived server-side and no client writes it.
 */
function leadCustomerValues(lead: Lead | null): LeadFormDataCustomer {
  const customer = leadCustomerRecord(lead)

  return {
    first_name: customer?.first_name ?? null,
    middle_name: customer?.middle_name ?? null,
    last_name: customer?.last_name ?? null,
    email: customer?.email ?? null,
    phone: customer?.phone ?? null,
    phone_2: customer?.phone_2 ?? null,
    phone_3: customer?.phone_3 ?? null,
    address: customer?.address ?? null,
    address_2: customer?.address_2 ?? null,
    city: customer?.city ?? null,
    state: customer?.state ?? null,
    zip_code: customer?.zip_code ?? null
  }
}

const customerFromSearchResult = (
  result: CustomerSearchResult
): LeadFormDataCustomer => ({
  first_name: result?.first_name ?? null,
  middle_name: result?.middle_name ?? null,
  last_name: result?.last_name ?? null,
  email: result?.email ?? null,
  phone: result?.phone ?? null,
  phone_2: result?.phone_2 ?? null,
  phone_3: result?.phone_3 ?? null,
  address: result?.address ?? null,
  address_2: result?.address_2 ?? null,
  city: result?.city ?? null,
  state: result?.state ?? null,
  zip_code: result?.zip_code ?? null
})

const leadCustomerName = (
  customer: LeadFormDataCustomer | LeadCustomer | null
) =>
  [customer?.first_name, customer?.middle_name, customer?.last_name]
    .filter(Boolean)
    .join(" ") || null

const leadCustomerAddressLine = (
  customer: LeadFormDataCustomer | LeadCustomer | null
) =>
  [
    customer?.address,
    customer?.address_2,
    customer?.city,
    customer?.state,
    customer?.zip_code
  ]
    .filter(Boolean)
    .join(", ") || null

export {
  customerFromSearchResult,
  isLinkedCustomer,
  leadCustomerAddressLine,
  leadCustomerName,
  leadCustomerRecord,
  leadCustomerValues
}
